import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, DatePicker, Form, Row, Table } from 'antd'
import BaseListing from 'src/BaseListings'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetAccountsSelectorVirtualized from 'src/widgets/Selectors/WidgetAccountsSelectorVirtualized'

const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 })

export default function IncomeTaxReport() {
  const Base = useMemo(() => new BaseListing('income_tax_report'), [])
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [branchId, setBranchId] = useState('0')
  const [accountId, setAccountId] = useState('0')
  const [paymentStatus, setPaymentStatus] = useState('')
  const [taxPercentage, setTaxPercentage] = useState('')
  const [paymentSectionId, setPaymentSectionId] = useState('')
  const [paymentSections, setPaymentSections] = useState([])
  const [branches, setBranches] = useState([])
  const [accounts, setAccounts] = useState([])
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [accountsReload, setAccountsReload] = useState(0)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [dateRange, setDateRange] = useState([])
  const companyName = wtGetFromLocalStorage('company_name')
  const { formatDate } = useMasterDateTimeFormats()

  useEffect(() => {
    Base.dropdownData(
      'income_tax_report_branches,income_tax_sections',
      {},
      [setBranches, setPaymentSections],
      1,
    )
  }, [Base])

  useEffect(() => {
    let active = true
    setAccounts([])
    setAccountId('0')
    setAccountsLoading(true)
    wtApiCall(
      'dropdown',
      { dropdown_names: 'income_tax_report_accounts', branch_id: branchId },
      'get',
      (response) => {
        if (!active) return
        setAccounts(response?.data?.[0]?.income_tax_report_accounts || [])
        setAccountsLoading(false)
      },
      (error) => {
        if (!active) return
        setAccountsLoading(false)
        lzNotifyErrorMessageFromResult(error)
      },
    )
    return () => {
      active = false
    }
  }, [branchId, accountsReload])

  const loadReport = () => {
    if (!accountId || accountId === '0') {
      lzNotifyErrorMessageFromResult('Please select an Account Head.')
      return
    }
    setLoading(true)
    wtApiCall(
      'reports/income_tax_report',
      {
        from_date: fromDate,
        to_date: toDate,
        branch_id: branchId,
        account_id: accountId,
        payment_status: paymentStatus,
        income_tax_section_id: paymentSectionId,
        tax_percentage: taxPercentage,
      },
      'get',
      (response) => {
        setRows(response.data || [])
        setLoading(false)
      },
      (error) => {
        setLoading(false)
        lzNotifyErrorMessageFromResult(error)
      },
    )
  }

  const exportReport = async () => {
    await wtApiPostWithContentTypeHandling('reports/income_tax_report/export', { data: rows })
  }

  const totalTaxable = rows.reduce((sum, row) => sum + Number(row.taxable_amount || 0), 0)
  const totalTax = rows.reduce((sum, row) => sum + Number(row.tax_amount || 0), 0)
  const taxPercentageOptions = [
    ...new Set(
      paymentSections.flatMap((section) =>
        String(section.tax_percentages || '')
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean),
      ),
    ),
  ].map((value) => ({ value, label: `${value}%` }))

  const columns = [
    { title: 'S#', key: 'serial', width: 70, render: (_, record, index) => index + 1 },
    ...[
      ['Date', 'jv_date', 120],
      ['Branch', 'branch', 150],
      ['Account Head', 'account_head', 250],
      ['Voucher #', 'voucher_number', 150],
      ['BPV Number', 'bpv_number', 150],
      ['PSID#', 'psid_no', 180],
      ['CPR', 'cpr_no', 180],
      ['Bank Name', 'bank_name', 180],
      ['Payment Date', 'payment_date', 120],
      ['Payment Section', 'payment_section', 300],
      ['Tax %', 'tax_percentage', 100],
      ['Taxable Amount', 'taxable_amount', 160],
      ['Tax Amount', 'tax_amount', 150],
      ['TaxPayer Name', 'taxpayer_name', 200],
      ['TaxPayer NTN', 'taxpayer_ntn', 150],
      ['TaxPayer CNIC', 'taxpayer_cnic', 180],
      ['TaxPayer City', 'taxpayer_city', 150],
      ['TaxPayer Address', 'taxpayer_address', 250],
      ['TaxPayer Status', 'taxpayer_status', 150],
      ['TaxPayer Business Name', 'taxpayer_business_name', 250],
      ['Month', 'psid_period', 130],
      ['Payment Mode', 'payment_mode', 150],
      ['Cheque No', 'cheque_no', 150],
      ['Tax Payment Status', 'tax_payment_status', 180],
      ['Payment Status', 'payment_status', 180],
    ].map(([title, dataIndex, width]) => ({
      title,
      dataIndex,
      key: dataIndex,
      width,
      ellipsis: dataIndex !== 'payment_section',
      ...(dataIndex === 'payment_section'
        ? { onCell: () => ({ style: { whiteSpace: 'normal', overflowWrap: 'anywhere' } }) }
        : {}),
      ...(['bank_name', 'taxpayer_address'].includes(dataIndex)
        ? { onCell: () => ({ style: { whiteSpace: 'normal', overflowWrap: 'anywhere' } }) }
        : {}),
      ...(['jv_date', 'payment_date'].includes(dataIndex)
        ? { render: (value) => formatDate(value) }
        : {}),
      ...(['taxable_amount', 'tax_amount'].includes(dataIndex)
        ? { align: 'right', render: formatAmount }
        : {}),
    })),
  ]

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form layout="vertical" onFinish={loadReport} style={{ maxWidth: '100%', margin: '0 auto' }}>
        <Card
          title={
            <span style={{ fontSize: '22px', marginLeft: '40%' }}>
              {[companyName, 'Income Tax Report'].filter(Boolean).join(' ')}
            </span>
          }
          style={{ width: '100%' }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} md={5}>
              <label>Date Range:</label>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={(value) => {
                  setDateRange(value || [])
                  setFromDate(value?.[0]?.format('YYYY-MM-DD') || '')
                  setToDate(value?.[1]?.format('YYYY-MM-DD') || '')
                }}
                style={{ width: '100%', height: 35 }}
              />
            </Col>
            <Col xs={24} sm={12} md={7}>
              <label className="account_dropdown_label_class">Account Head: *</label>
              <WidgetAccountsSelectorVirtualized
                data={accounts}
                value={accountId}
                height={35}
                disabled={accountsLoading}
                onChange={(value) => setAccountId(value ?? '0')}
                reloadDropdownData={() => setAccountsReload((value) => value + 1)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                branches={[
                  { value: '0', label: 'All Branches' },
                  ...branches.map((branch) => ({
                    value: String(branch.value || branch.id),
                    label: branch.label || branch.name,
                  })),
                ]}
                onChange={(value) => {
                  setBranchId(value.value)
                  setAccountId('0')
                  setRows([])
                }}
                showCloudIcon={false}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <label>Payment Status:</label>
              <WidgetSelector
                selectedValue={paymentStatus}
                options={[
                  { value: '', label: 'All' },
                  { value: 'pending_approval', label: 'Pending Approval' },
                  { value: 'approved', label: 'Approved' },
                ]}
                showCloudIcon={false}
                onChange={(value) => setPaymentStatus(value.value)}
              />
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} sm={12} md={5}>
              <label>Payment Section:</label>
              <WidgetSelector
                selectedValue={paymentSectionId}
                options={[
                  { value: '', label: 'All Payment Sections' },
                  ...paymentSections.map((section) => ({
                    value: section.value,
                    label: section.tax_payment_section || section.section || section.label,
                  })),
                ]}
                showCloudIcon={false}
                onChange={(value) => setPaymentSectionId(value.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={3}>
              <label>Tax %:</label>
              <WidgetSelector
                selectedValue={taxPercentage}
                options={[{ value: '', label: 'All' }, ...taxPercentageOptions]}
                showCloudIcon={false}
                onChange={(value) => setTaxPercentage(value.value)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                style={{ marginTop: 30, marginRight: 5 }}
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                Search
              </Button>
              <Button
                style={{ marginTop: 30, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportReport}
                disabled={!rows.length || loading}
              >
                Export
              </Button>
            </Col>
          </Row>
          <br />
          <Table
            columns={columns}
            dataSource={rows}
            rowKey="detail_id"
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 4000, y: 400 }}
            loading={loading}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={11}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11} align="right">
                  <strong>{formatAmount(totalTaxable)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={12} align="right">
                  <strong>{formatAmount(totalTax)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={13} colSpan={12} />
              </Table.Summary.Row>
            )}
          />
        </Card>
      </Form>
    </div>
  )
}
